import { useCallback, useMemo } from 'react'
import { useMutation } from '@apollo/client/react'
import { SearchList01Icon } from '@hugeicons/core-solid-rounded'
import { Trans, useLingui } from '@lingui/react/macro'

import {
  AdminAPI,
  GraphQL,
  makeFragmentData,
  SharedAPI
} from '@guestgoodie/graphql'

import Button from '@/components/ui/Button'
import FilterSelect from '@/components/ui/FilterSelect'
import Input from '@/components/ui/Input'
import { GoodieTable } from '@/features/goodies'
import { useGoodieTable } from '@/features/goodies'
import { DialogHeader } from '@/layouts/Dialog'
import { helpers } from '@/features/goodies'

export default function AddGoodies({
  category,
  onDone
}: {
  category: GraphQL.CategoryFragmentFragment
  onDone: () => void
}) {
  const { t } = useLingui()

  const goodieTypes = helpers.useGoodieTypeOptions()

  const filteredGoodieTypes = useMemo(() => {
    return goodieTypes.filter(type => type.value !== GraphQL.GoodieType.Message)
  }, [goodieTypes])

  const possibleTypes = useMemo(
    () => filteredGoodieTypes.map(({ value }) => value),
    [filteredGoodieTypes]
  )

  const [update, { loading }] = useMutation(AdminAPI.category.UPDATE, {
    refetchQueries: [AdminAPI.category.GET_BY_LAYOUT_AND_SLUG]
  })

  const {
    queryVariables,
    selectedGoodies,
    types,
    search,
    onSelectGoodie,
    setSearch,
    setTypes,
    updateSort
  } = useGoodieTable(
    possibleTypes,
    category.categoryGoodies.map(({ goodieId }) => goodieId)
  )

  const onAdd = useCallback(() => {
    if (selectedGoodies.size === 0) return

    const goodieIds = Array.from(selectedGoodies)

    update({
      variables: {
        input: {
          id: category.id,
          goodieIds
        }
      },
      optimisticResponse: {
        updateCategory: makeFragmentData(
          {
            ...category,
            categoryGoodies: goodieIds.map((goodieId, position) => ({
              goodieId,
              position
            }))
          },
          SharedAPI.category.CATEGORY_FRAGMENT
        )
      }
    }).finally(onDone)
  }, [selectedGoodies, update, category, onDone])

  const categoryName = category.name

  return (
    <div className="flex flex-col gap-5">
      <DialogHeader
        title={t`Add/Remove Category Goodies`}
        description={t`Add or remove Goodies from the category with the name "${categoryName}"`}
      />
      <div className="flex w-full flex-row gap-2.5 pb-6">
        <FilterSelect
          options={filteredGoodieTypes}
          selectedValues={types}
          onChange={setTypes}
          placeholder={t`All types`}
        />
        <Input
          Icon={SearchList01Icon}
          placeholder={t`Search…`}
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
      </div>
      <GoodieTable
        selectedIds={selectedGoodies}
        onSelect={onSelectGoodie}
        variables={queryVariables}
        onChangeSort={updateSort}
        hideButtons
        shouldSelectInsteadOfEdit
      />
      <div className="flex flex-row justify-between">
        <div>
          <Button variant="outline" onClick={onDone}>
            <Trans>Cancel</Trans>
          </Button>
        </div>
        <div>
          <Button
            disabled={selectedGoodies.size === 0}
            loading={loading}
            onClick={onAdd}
          >
            <Trans>Save</Trans>
          </Button>
        </div>
      </div>
    </div>
  )
}
